// src/utils/paletteUtils.js
// Updated to respect theme-specific link background colors

/**
 * Applies a palette's colors to the document's CSS variables
 * @param {Object} palette - The palette object containing colors array and linkOpacity
 */
export const applyPalette = (palette) => {
  if (!palette || !palette.colors || !Array.isArray(palette.colors)) {
    console.warn('Invalid palette format', palette);
    return;
  }
  
  const colors = palette.colors;
  
  // Apply colors to CSS variables
  if (colors.length >= 5) {
    // Map palette colors to CSS variables
    // Important: The order of colors in the palette is different from how they're used in the CSS
    // Based on the PreviewUserProfile.js and Dashboard.module.css
    document.documentElement.style.setProperty('--main-color', colors[0]);  // Main color (used for headings, active elements)
    document.documentElement.style.setProperty('--secondary-color', colors[1]);  // Secondary color (accent elements)
    document.documentElement.style.setProperty('--third-color', colors[4]); // Background color (4th palette item is background in PreviewUserProfile)
    document.documentElement.style.setProperty('--background-blur', colors[3]); // Container background
    document.documentElement.style.setProperty('--border-color', colors[2]); // Borders and dividers
    
    // *** IMPORTANT: Only set link background from palette if no theme-specific color is present ***
    // Check if a theme has already set a link background color
    const computedStyle = getComputedStyle(document.documentElement);
    const themeBackgroundColor = computedStyle.getPropertyValue('--theme-link-background').trim();
    
    // Only set link-background if theme hasn't specified one (it returns 'null' as string if not set)
    if (themeBackgroundColor === 'null' || themeBackgroundColor === '') {
      document.documentElement.style.setProperty('--link-background', colors[0]); // Use primary color for links
      console.log('Applied palette color to links:', colors[0]);
    } else {
      console.log('Using theme-specific link background color, not applying palette color');
    }
    
    document.documentElement.style.setProperty('--link-border-color', colors[2]); // Use border color from palette
    
    // Create custom gradients and colors for the navigation based on the palette
    // Use transparency to create subtle effects
    const navGradient = `linear-gradient(135deg, ${colors[4]}40 0%, ${colors[3]}80 100%)`;
    document.documentElement.style.setProperty('--nav-background', navGradient);
    
    // Set colors for menu states
    document.documentElement.style.setProperty('--menu-hover-bg', `${colors[0]}20`); // Hover state with 20% opacity
    document.documentElement.style.setProperty('--menu-active-bg', `${colors[3]}80`); // Active state with 80% opacity
    
    // Banner and action buttons
    document.documentElement.style.setProperty('--banner-bg', colors[0]);
    document.documentElement.style.setProperty('--action-button-bg', colors[0]);
    document.documentElement.style.setProperty('--action-button-border', colors[1]);
    
    // Set link background opacity separately based on linkOpacity property
    if (palette.linkOpacity !== undefined) {
      // Set the CSS variable for background opacity
      document.documentElement.style.setProperty('--link-background-opacity', palette.linkOpacity / 100);
    } else {
      document.documentElement.style.setProperty('--link-background-opacity', 1); // Default to fully opaque
    }
    
    console.log('Palette applied successfully', colors, 'Link background opacity:', palette.linkOpacity);
  } else {
    console.warn('Palette has fewer than 5 colors', colors);
  }
};

/**
 * Resets palette to default colors
 */
export const resetPalette = () => {
  document.documentElement.style.setProperty('--main-color', '#000000');
  document.documentElement.style.setProperty('--secondary-color', '#255716c4');
  document.documentElement.style.setProperty('--third-color', '#ffffff');
  document.documentElement.style.setProperty('--background-blur', '#01f5fd0e');
  document.documentElement.style.setProperty('--border-color', '#8b8888');
  
  // Reset link background and border colors only if no theme color is set
  const computedStyle = getComputedStyle(document.documentElement);
  const themeBackgroundColor = computedStyle.getPropertyValue('--theme-link-background').trim();
  
  if (themeBackgroundColor === 'null' || themeBackgroundColor === '') {
    document.documentElement.style.setProperty('--link-background', '#000000');
  }
  
  document.documentElement.style.setProperty('--link-border-color', '#8b8888');
  
  // Reset custom properties for dashboard
  document.documentElement.style.setProperty('--nav-background', 'linear-gradient(135deg, #f1f1c0 0%, #ccacac 100%)');
  document.documentElement.style.setProperty('--menu-hover-bg', '#ffc40052');
  document.documentElement.style.setProperty('--menu-active-bg', '#ffffff56');
  document.documentElement.style.setProperty('--banner-bg', '#000000');
  document.documentElement.style.setProperty('--action-button-bg', '#000000');
  document.documentElement.style.setProperty('--action-button-border', '#ffffff');
  
  // Reset link background opacity to default (fully opaque)
  document.documentElement.style.setProperty('--link-background-opacity', '1');
};

/**
 * Adjusts color opacity for a hex color
 * @param {string} color - Hex color code
 * @param {number} opacity - Opacity value (0-100)
 * @returns {string} RGBA color string
 */
export const adjustColorOpacity = (color, opacity) => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
};

/**
 * Gets processed colors from a palette with opacity and reverse options applied
 * @param {Object} palette - The palette object
 * @param {boolean} isReversed - Whether to reverse the colors
 * @param {number} opacity - Opacity value (0-100)
 * @returns {Array} Array of processed color strings
 */
export const getProcessedColors = (palette, isReversed = false, opacity = 100) => {
  if (!palette || !palette.colors) return [];
  
  let colors = [...palette.colors];
  if (isReversed) {
    colors = colors.reverse();
  }
  
  return colors.map((color) => adjustColorOpacity(color, opacity));
};